import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Supabase configuration missing!');
  console.error('Please add to .env:');
  console.error('- REACT_APP_SUPABASE_URL');
  console.error('- REACT_APP_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Required Supabase table schema:
// Run this SQL in your Supabase SQL Editor if you don't have the table yet:
//
// CREATE TABLE IF NOT EXISTS generated_ads (
//   id BIGSERIAL PRIMARY KEY,
//   user_id TEXT NOT NULL,
//   ad_id TEXT NOT NULL,
//   prompt TEXT,
//   image_url TEXT NOT NULL,
//   caption TEXT,
//   aspect_ratio TEXT DEFAULT '1:1',
//   width INTEGER DEFAULT 1024,
//   height INTEGER DEFAULT 1024,
//   model TEXT,
//   parent_ad_id TEXT,
//   is_edit BOOLEAN DEFAULT FALSE,
//   created_at TIMESTAMPTZ DEFAULT NOW()
// );
// 
// CREATE INDEX idx_generated_ads_user_id ON generated_ads(user_id);
// CREATE INDEX idx_generated_ads_created_at ON generated_ads(created_at DESC);
// CREATE INDEX idx_generated_ads_parent_ad_id ON generated_ads(parent_ad_id);

// Helper functions for user data
// Helper to upload image to Supabase Storage
export const uploadImageToStorage = async (dataUrl, adId) => {
  try {
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    
    const fileName = `ads/${adId}.png`;
    
    // Upload to Supabase Storage
    const { error } = await supabase.storage
      .from('generated-ads')
      .upload(fileName, blob, {
        contentType: 'image/png',
        upsert: true
      });
    
    if (error) {
      console.error('Upload error:', error);
      return null;
    }
    
    // Get public URL
    const { data } = supabase.storage
      .from('generated-ads')
      .getPublicUrl(fileName);
    
    const publicUrl = data.publicUrl;
    console.log('✅ Image uploaded to Supabase Storage');
    console.log('📍 Public URL:', publicUrl);
    
    return publicUrl;
  } catch (error) {
    console.error('Failed to upload image:', error);
    return null;
  }
};

export const userDataService = {
  // Get user data
  async getUserData(userId) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching user data:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Exception fetching user data:', error);
      return null;
    }
  },

  // Create or update user data
  async upsertUserData(userData) {
    try {
      const { data, error } = await supabase
        .from('users')
        .upsert(userData, {
          onConflict: 'user_id'
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error upserting user data:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Exception upserting user data:', error);
      return null;
    }
  },

  // Update generation count
  async updateGenerationCount(userId, newCount) {
    try {
      const { error } = await supabase
        .from('users')
        .update({ generation_count: newCount, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating generation count:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Exception updating generation count:', error);
      return false;
    }
  },

  // Update subscription
  async updateSubscription(userId, subscriptionData) {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          subscription_plan: subscriptionData.planId,
          subscription_start_date: subscriptionData.startDate,
          generation_limit: subscriptionData.generationsLimit,
          stripe_customer_id: subscriptionData.stripeCustomerId || null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating subscription:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Exception updating subscription:', error);
      return false;
    }
  },

  // Save generated ad to history
  async saveGeneratedAd(userId, adData, parentAdId = null) {
    try {
      const { error } = await supabase
        .from('generated_ads')
        .insert({
          user_id: userId,
          ad_id: adData.id,
          prompt: adData.prompt,
          image_url: adData.imageUrl,
          caption: adData.caption || null,
          aspect_ratio: adData.aspectRatio || '1:1',
          width: adData.width || 1024,
          height: adData.height || 1024,
          model: adData.model,
          parent_ad_id: parentAdId,
          is_edit: !!parentAdId,
          created_at: adData.createdAt
        });
      
      if (error) {
        console.error('Error saving generated ad:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Exception saving generated ad:', error);
      return false;
    }
  },

  // Get user's ad history
  async getUserAdHistory(userId, limit = 100) {
    try {
      const { data, error } = await supabase
        .from('generated_ads')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('Error fetching ad history:', error);
        return [];
      }
      
      return data.map(ad => ({
        id: ad.ad_id,
        prompt: ad.prompt,
        imageUrl: ad.image_url,
        caption: ad.caption,
        aspectRatio: ad.aspect_ratio || '1:1',
        width: ad.width || 1024,
        height: ad.height || 1024,
        model: ad.model,
        createdAt: ad.created_at,
        parentAdId: ad.parent_ad_id,
        isEdit: ad.is_edit
      }));
    } catch (error) {
      console.error('Exception fetching ad history:', error);
      return [];
    }
  }
};

